import React, { useCallback, useEffect } from 'react';
import { connect } from 'react-redux';
import { Form, Button, Row, Col, Card } from 'react-bootstrap';
import { PageHeader, Loading } from '../../components';
import MerchantSelect from '../../components/MerchantSelect/MerchantSelect';
import useComponentState from '../../utils/useComponentState';
import styles from './AddMerchantGroups.module.scss';
import { toast } from 'react-toastify';
import { addMerchantGroupApi, getAllMerchant } from '../../api/merchant';
import { RESPONSE_MESSAGE, TOAST_MESSAGES, INITIAL_STATE, VALIDATION_ERROR_MESSAGES } from './AddMerchantGroupsConstants';

const AddMerchantGroups = ({ auth }) => {
  const [state, setState] = useComponentState(INITIAL_STATE);

  // Fetch merchants on mount
  useEffect(() => {
    const fetchMerchants = async () => {
      try {
        setState({ merchantsLoading: true });
        const response = await getAllMerchant(auth?.auth?.token);
        const list = Array.isArray(response?.data) ? response.data : Array.isArray(response) ? response : response?.items || [];
        setState({ merchants: list, merchantsLoading: false });
      } catch (err) {
        setState({ merchantsLoading: false });
        toast.error(TOAST_MESSAGES.merchantsError);
      }
    };
    fetchMerchants();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth?.auth?.token]);

  const handleInputChange = useCallback((e) => {
    if (!e || !e.target) return;
    const { name, value } = e.target;

    if (name === 'merchantGroupId') {
      setState({ merchantGroupId: value, merchantGroupIdError: null });
    } else if (name === 'groupName') {
      setState({ groupName: value, groupNameError: null });
    } else if (name === 'description') {
      setState({ description: value, descriptionError: null });
    }
    else if (name === 'imageUrl') {
      setState({ imageUrl: value, imageUrlError: null });
      // Real-time validation for image URL
      if (value.trim() && !value.trim().startsWith('https://')) {
        setState({ imageUrlError: VALIDATION_ERROR_MESSAGES.imageUrl });
      }
    }
  }, [setState]);

  const handleMerchantSelect = useCallback((selectedIds) => {
    setState({ merchantIds: selectedIds, merchantIdError: null });
  }, [setState]);

  const validateImageUrl = useCallback(() => {
    if (!state.imageUrl.trim()) {
      setState({ imageUrlError: VALIDATION_ERROR_MESSAGES.imageUrlRequired });
      return false;
    }
    if (!state.imageUrl.trim().startsWith('https://')) {
      setState({ imageUrlError: VALIDATION_ERROR_MESSAGES.imageUrl });
      return false;
    }
    return true;
  }, [state.imageUrl, setState]);

  const validateMerchantGroupId = useCallback(() => {
    if (!state.merchantGroupId.trim()) {
      setState({ merchantGroupIdError: VALIDATION_ERROR_MESSAGES.merchantGroupId });
      return false;
    }
    return true;
  }, [state.merchantGroupId, setState]);

  const validateGroupName = useCallback(() => {
    if (!state.groupName.trim()) {
      setState({ groupNameError: VALIDATION_ERROR_MESSAGES.groupName });
      return false;
    }
    return true;
  }, [state.groupName, setState]);

  const validateDescription = useCallback(() => {
    if (!state.description.trim()) {
      setState({ descriptionError: VALIDATION_ERROR_MESSAGES.description });
      return false;
    }
    return true;
  }, [state.description, setState]);

  const validateMerchantIds = useCallback(() => {
    if (!Array.isArray(state.merchantIds) || state.merchantIds.length === 0) {
      setState({ merchantIdError: VALIDATION_ERROR_MESSAGES.merchantId });
      return false;
    }
    return true;
  }, [state.merchantIds, setState]);

  const validateForm = useCallback(() => {
    let isValid = true;

    if (!validateMerchantGroupId()) {
      isValid = false;
    }
    if (!validateGroupName()) {
      isValid = false;
    }
    if (!validateMerchantIds()) {
      isValid = false;
    }
    if (!validateDescription()) {
      isValid = false;
    }
    if (!validateImageUrl()) {
      isValid = false;
    }

    return isValid;
  }, [validateMerchantGroupId, validateGroupName, validateMerchantIds, validateDescription, validateImageUrl]);

  const handleSubmit = useCallback(async (event) => {
    event.preventDefault();
    const form = event.currentTarget;

    if (form.checkValidity() === false) {
      event.stopPropagation();
      setState({ validated: true });
      return;
    }

    if (!validateForm()) {
      return;
    }

    setState({ loading: true });

    try {
      const payload = {
        merchantGroupId: state.merchantGroupId,
        name: state.groupName,
        description: state.description,
        imageUrl: state.imageUrl,
        merchantIds: state.merchantIds,
      };

      const response = await addMerchantGroupApi(payload, auth.auth.token);

      if (response.message === RESPONSE_MESSAGE) {
        toast.success(TOAST_MESSAGES.success);
        setState(INITIAL_STATE);
      } else {
        const errorMessage = response.message || TOAST_MESSAGES.error;
        setState({ loading: false });
        toast.error(errorMessage);
      }
    } catch (error) {
      setState({ loading: false });
      toast.error(TOAST_MESSAGES.error);
    }
  }, [state.merchantGroupId, state.groupName, state.description, state.imageUrl, state.merchantIds, auth, validateForm, setState]);

  const handleCancel = useCallback(() => {
    setState(INITIAL_STATE);
  }, [setState]);

  const renderInputField = useCallback((name, label, value, onChange, max, rows) => {
    let error = null;
    if (name === 'merchantGroupId') error = state.merchantGroupIdError;
    else if (name === 'groupName') error = state.groupNameError;
    else if (name === 'description') error = state.descriptionError;
    else if (name === 'imageUrl') error = state.imageUrlError;

    return (
      <Form.Group as={Row} className='mb-3' key={name}>
        <Form.Label column sm={3}>
          {label} <span className='text-danger'>*</span>
        </Form.Label>
        <Col sm={9}>
          <Form.Control
            as={rows ? 'textarea' : 'input'}
            type={rows ? undefined : 'text'}
            name={name}
            value={value}
            onChange={onChange}
            placeholder={name === 'imageUrl' ? 'https://example.com/image.png' : `Enter ${label.toLowerCase()}`}
            maxLength={max}
            rows={rows}
            required
          />
          <Form.Control.Feedback type='invalid'>
            {label} cannot be empty
          </Form.Control.Feedback>
          {error && <small className={styles.error_msg}>{error}</small>}
        </Col>
      </Form.Group>
    );
  }, [state.merchantGroupIdError, state.groupNameError, state.descriptionError, state.imageUrlError]);

  return (
    <div className={styles.root_div}>
      <PageHeader text='Add Merchant Group' />

      <Card className={styles.formCard}>
        <Card.Body>
          <Form noValidate validated={state.validated} onSubmit={handleSubmit}>
            {renderInputField(
              'merchantGroupId',
              'Merchant Group ID',
              state.merchantGroupId,
              handleInputChange,
              50
            )}

            <MerchantSelect
              merchants={state.merchants}
              selectedMerchants={state.merchantIds}
              onMerchantSelect={handleMerchantSelect}
              loading={state.merchantsLoading}
              required={true}
              label='Merchants'
            />
            {state.merchantIdError && (
              <Form.Group as={Row} className='mb-3'>
                <Form.Label column sm={3} />
                <Col sm={9}>
                  <small className={styles.error_msg}>{state.merchantIdError}</small>
                </Col>
              </Form.Group>
            )}

            {renderInputField(
              'groupName',
              'Group Name',
              state.groupName,
              handleInputChange,
              100
            )}

            {renderInputField(
              'description',
              'Description',
              state.description,
              handleInputChange,
              500,
              3
            )}

            {renderInputField(
              'imageUrl',
              'Image URL',
              state.imageUrl,
              handleInputChange,
              320
            )}
          </Form>

          {state.loading && <Loading />}
        </Card.Body>
        <div className={styles.formActions}>
          <Button variant='secondary' className='me-2' onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant='primary'
            disabled={state.loading}
          >
            {state.loading ? 'Adding...' : 'Add Merchant Group'}
          </Button>
        </div>
      </Card>
    </div>
  );
};


const mapStateToProps = (state) => ({
  auth: state.auth,
});

export default connect(mapStateToProps)(AddMerchantGroups);